/**
 * Voice Cast — speaker → ElevenLabs voice configuration.
 *
 * Two kinds of entries:
 *   1. Named NPCs (Lens Aldric, Rhovane, ...). Keyed by the speaker name as it
 *      appears in briefings.ts (line.name).
 *   2. Party voices, one per (race, gender). Keyed `party_{race}_{gender}`.
 *      Used for any party-line variant that resolves to that race+gender.
 *
 * The `direction` field is a Voice Design prompt — paste it directly into
 * https://elevenlabs.io/app/voice-design to generate a custom voice, OR use it
 * as a casting brief while browsing the Voice Library. Each prompt leads with
 * audio qualities (pitch, timbre, pace, accent) and ends with a brief character
 * anchor and anti-clichés to dodge.
 *
 * Once you generate or pick a voice, paste its voice_id below.
 *
 * Models:
 *   - eleven_multilingual_v2     DEFAULT. PVCs (which most working casts are) require v2;
 *                                v3 silently ignores tags on PVC voices and adds artifacts.
 *                                Tags are stripped at gen time when model is v2.
 *   - eleven_v3                  opt-in for non-PVC voices where you want tag responsiveness
 *                                (Voice Design output, IVC clones, Default Pro library voices)
 *   - eleven_turbo_v2_5          --draft mode: cheap iteration while tuning text
 *
 * Voice settings tuning:
 *   - stability       0.0 (expressive, varied) ↔ 1.0 (consistent, flat). 0.4-0.6 for narrative.
 *   - similarity_boost 0.0 ↔ 1.0. 0.7-0.85 standard. Higher = closer to source clone.
 *   - style           0.0 ↔ 1.0. v3-only. Pushes performance further from neutral. 0.3-0.5 for drama.
 *   - use_speaker_boost  true for clarity on quiet voices.
 */

export type ElevenLabsModel =
  | 'eleven_v3'
  | 'eleven_multilingual_v2'
  | 'eleven_turbo_v2_5';

export interface VoiceSettings {
  stability?: number;
  similarity_boost?: number;
  style?: number;
  use_speaker_boost?: boolean;
}

export interface VoiceCastEntry {
  /** ElevenLabs voice ID — paste from Voice Library. Empty string = unset, generation will fail loudly. */
  voice_id: string;
  /** Defaults to eleven_v3. */
  model?: ElevenLabsModel;
  voice_settings?: VoiceSettings;
  /** Casting direction — what voice to look for in the library. */
  direction: string;
}

/* ── Named NPCs (Acts 1-2) ───────────────────────────────────────────── */

const NPCS: Record<string, VoiceCastEntry> = {
  'Lens Aldric': {
    voice_id: '7p1Ofvcwsv7UBPoFNcpI',
    model: 'eleven_multilingual_v2',
    voice_settings: { stability: 0.95, similarity_boost: 0.8, style: 0.4, use_speaker_boost: true },
    direction: `Native English. Male, mid-50s. Excellent audio quality. Persona: rural priest in a logging town. Emotion: weary, plainspoken, quietly resolved. Low baritone with gravel and bottom-end weight, slight smoke-rasp from preaching outdoors. Slow measured pace like he chooses every word twice and discards the first. Working American accent with a faint Pacific Northwest or Midwestern lilt, lots of breath between thoughts. Avoid sermonic, theatrical, villainous-cleric, or BBC-vicar delivery.`,
  },
  'Steward Maren': {
    voice_id: '8hcTQbQtx4uFCMEiTbGE',
    model: 'eleven_multilingual_v2',
    voice_settings: { stability: 0.5, similarity_boost: 0.8, style: 0.25, use_speaker_boost: true },
    direction: `Native English. Female, late 40s. Excellent audio quality. Persona: small-town civic leader, fire chief, foreman. Emotion: weathered, steady, faintly amused. Contralto with chest resonance, faint vocal fry in casual moments and full steady delivery on orders. Pace is unhurried but every word lands hard. Working American accent with a faint Midwestern or rural Western lilt, slight age-rasp from outdoor work and shouting over wind. Avoid posh, aristocratic, mom-warm, or husky-femme-fatale delivery.`,
  },
  'Scholar Aevryn': {
    voice_id: '',
    model: 'eleven_multilingual_v2',
    voice_settings: { stability: 0.6, similarity_boost: 0.85, style: 0.35, use_speaker_boost: true },
    direction: `Native English. Male, ageless (could read 30 or 300). Excellent audio quality. Persona: museum docent, forensic anthropologist, poet reading their own work. Emotion: calm, intellectual, quietly intrigued. Light tenor with a breathy edge and very precise consonants, tasting each word before it leaves. Slow unhurried pace with comfortable silences. Faint mid-Atlantic accent, slightly placeless like he has lived in many libraries, with subtle melodic lift on closing syllables. Avoid stuffy British professor, wispy fey-elf, or villainous-cult-leader delivery.`,
  },
  'Rhovane': {
    voice_id: 'kqVT88a5QfII1HNAEPTJ',
    model: 'eleven_multilingual_v2',
    voice_settings: { stability: 0.45, similarity_boost: 0.75, style: 0.45, use_speaker_boost: true },
    direction: `Native English. Male, mid-30s. Excellent audio quality. Persona: noir bartender, quartermaster on a smuggling ship. Emotion: dry, sardonic, faintly bored. Low baritone at half-volume, dry sandpaper texture with faint rasp from cigar smoke and undercity air. Slow clipped pace with hard stops, flat affect like every sentence is a transaction he has already done the math on. Working American accent with a faint hard-Eastern edge, never raises his voice. Avoid theatrical villain, vampire-Lugosi, noir-narrator, or shouting delivery.`,
  },
  'Vesper Thalane': {
    voice_id: 'flHkNRp1BlvT73UL6gyz',
    model: 'eleven_multilingual_v2',
    voice_settings: { stability: 0.55, similarity_boost: 0.8, style: 0.4, use_speaker_boost: true },
    direction: `Native English. Female, mid-30s. Excellent audio quality. Persona: forensic pathologist who is great at parties. Emotion: cool, composed, quietly amused. Mid alto with cool clarity, never breathy, slightly formal cadence. Even unhurried pace, very clean articulation, subtle smile under the surface like she finds her grim subject matter mildly delightful. Faint mid-Atlantic or East Coast academic accent, well-educated but not posh. Avoid breathy-vampire seductress, goth-witch, hooded-villain, or sing-song delivery.`,
  },
};

/* ── Party voices: 2 per race (male + female) ────────────────────────── */

export const RACES = [
  'human',
  'caligari',
  'anthura',
  'granocen',
  'thalassan',
  'vael',
  'serath',
] as const;
export type Race = (typeof RACES)[number];

export const GENDERS = ['male', 'female'] as const;
export type Gender = (typeof GENDERS)[number];

export function partyKey(race: Race | string, gender: Gender | string): string {
  return `party_${race}_${gender}`;
}

/** Per-race party voices. PASTE voice_id strings here — one for each (race, gender)
 *  bucket. `model` and `voice_settings` are optional; they fall back to v3 + the
 *  PARTY_DEFAULT_SETTINGS below. */
type PartyVoiceConfig = {
  voice_id: string;
  direction: string;
  model?: ElevenLabsModel;
  voice_settings?: VoiceSettings;
};

const PARTY_DEFAULT_SETTINGS: VoiceSettings = {
  stability: 0.5,
  similarity_boost: 0.8,
  style: 0.3,
  use_speaker_boost: true,
};

const PARTY_VOICES_BY_RACE: Record<Race, { male: PartyVoiceConfig; female: PartyVoiceConfig }> = {
  human: {
    male: {
      voice_id: 'tZssYepgGaQmegsMEXjK',
      model: 'eleven_multilingual_v2',
      direction: `Native English. Male, late 20s to mid-30s. Excellent audio quality. Persona: young paramedic, off-duty firefighter, sergeant up from the ranks. Emotion: grounded, naturalistic, plainspoken. Mid-range warm baritone with a slight bottom-edge, no high register. Even conversational pace, lands every word without underselling it. Standard working-class American accent with slight outdoor-lived-in quality. Avoid posh, breathy, theatrical, or anime-protagonist delivery.`,
    },
    female: {
      voice_id: 'iCrDUkL56s3C8sCRl7wb',
      model: 'eleven_v3',
      direction: `Native English. Female, late 20s to mid-30s. Excellent audio quality. Persona: EMT, young farm owner, off-duty firefighter. Emotion: grounded, plainspoken, quietly capable. Mid alto, warm but grounded with no helium. Even direct pace, no pauses for drama, faint smile in casual lines and all business when needed. Standard working-class American accent with slight outdoor lived-in quality. Avoid posh, breathy, husky-femme-fatale, mom-warm, or sing-song delivery.`,
    },
  },
  caligari: {
    male: {
      voice_id: 'jhBzyKbsdeM6F66SZCaK',
      direction: `Native English. Male, mid-30s. Excellent audio quality. Persona: noir bartender, tired homicide detective end-of-shift, low-stakes con man. Emotion: dry, sardonic, half-amused. Low baritone at low volume, dry sandpaper texture like a smoker who quit five years ago. Slow deliberate pace, flat affect, never animated. Working-class American accent with a faint hard-Eastern edge. Avoid Bela Lugosi, vampire, theatrical-villain, or shouting delivery.`,
    },
    female: {
      voice_id: '4BAlflaQyhIcCfHiEI7x',
      direction: `Native English. Female, mid-30s. Excellent audio quality. Persona: homicide detective, tax attorney everyone is afraid of, fence who knows the value of every stolen item. Emotion: cool, ironic, world-weary. Low-mid alto with chest resonance, quiet but with sharp consonants. Slow deliberate pace, subtle smile of someone who has seen the trick before. Faint working-class East Coast American accent. Avoid breathy-seductress, goth-witch, or sing-song delivery.`,
    },
  },
  anthura: {
    male: {
      voice_id: '',
      direction: `Native English. Male, late 20s. Excellent audio quality. Persona: young naturalist on the Appalachian Trail, documentary host genuinely awed by what they are filming. Emotion: warm, curious, alert. Bright mid-range tenor with a slight curious lilt, slightly faster cadence like every sentence might be a question. Subtle breath excitement on new ideas. Faint Pacific Northwest or upper-Midwest American accent, somewhere with woods. Avoid breathy forest-druid, wispy nature-spirit, or stoner-zen delivery.`,
    },
    female: {
      voice_id: 'WAhoMTNdLdMoq1j3wf3I',
      model: 'eleven_v3',
      direction: `Native English. Female, late 20s. Excellent audio quality. Persona: young naturalist on her first major expedition, museum educator who lights up around children. Emotion: warm, curious, playful. Bright clear alto with a playful lift, slightly faster pace and small upward tilt at sentence ends. Subtle smile under most lines. Faint Pacific Northwest or rural-academic American accent. Avoid wispy nature-spirit, fey-pixie, or breathy-yoga-instructor delivery.`,
    },
  },
  granocen: {
    male: {
      voice_id: 'bwCXcoVxWNYMlC6Esa8u',
      direction: `Native English. Male, age ambiguous (sounds 50 but could be 200). Excellent audio quality. Persona: old shipwright, retired Marine drill instructor who never raises his voice, stonemason watching apprentices fail. Emotion: patient, weighty, calm. Deep bass with chest resonance, every word fully voiced, resonant low end like a cello. Slow patient pace. American accent, no specific region, just deep and weathered. Avoid shouting-brute, gravel-dwarf, angry-giant, or comic-low-troll delivery.`,
    },
    female: {
      voice_id: 'Qggl4b0xRMiqOwhPtVWT',
      direction: `Native English. Female, age ambiguous (sounds 50 but could be older). Excellent audio quality. Persona: retired Coast Guard captain, master quilter who runs the town, geological surveyor. Emotion: calm, grave, steady. Low alto with faint resonance and gravity, faint stone-on-stone in the lowest register. Slow deliberate pace, quiet authority — the room listens when she speaks. American accent, mid or southwestern weathered. Avoid soft-motherly, fragile, or breathy delivery.`,
    },
  },
  thalassan: {
    male: {
      voice_id: 'HAvvFKatz0uu0Fv55Riy',
      direction: `Native English. Male, mid-30s. Excellent audio quality. Persona: yacht-club harbormaster, maritime law professor, cellist explaining their instrument. Emotion: composed, smooth, slightly cosmopolitan. Mid tenor with smooth fluid delivery, slight musicality on consonants. Unhurried pace with occasional rolling phrasing where consonants almost flow into each other. Faint Mediterranean or coastal-academic American accent, well-traveled but not affected. Avoid literally-aquatic, gurgling, surfer-dude, or pirate delivery.`,
    },
    female: {
      voice_id: 'qSeXEcewz7tA0Q0qk9fH',
      direction: `Native English. Female, mid-30s. Excellent audio quality. Persona: marine biologist on a documentary, foreign correspondent doing live commentary, museum docent in the natural-history wing. Emotion: composed, smooth, gently curious. Mid alto with smooth even delivery and clean diction, slightly formal cadence and faint musicality. Even-keeled pace, never rushed, sounds like she enjoys remembering what she is reciting. Faint Mediterranean or coastal-academic American accent. Avoid mermaid, breathy, sing-song, or seductress delivery.`,
    },
  },
  vael: {
    male: {
      voice_id: '',
      direction: `Native English. Male, ageless (could read 30 or 300). Excellent audio quality. Persona: museum curator describing a rare manuscript, literature professor reading aloud, planetarium docent. Emotion: calm, precise, faintly otherworldly. Light tenor with breathy edge and very precise consonants. Slow deliberate pace, comfortable with silence, subtle melodic rise on closing phrases. Faint mid-Atlantic accent, slightly placeless and well-read. Avoid elf-wizard, stuffy British professor, breathy-fey, or whispered-cult delivery.`,
    },
    female: {
      voice_id: 'pjcYQlDFKMbcOUp6F5GD',
      direction: `Native English. Female, ageless. Excellent audio quality. Persona: documentary narrator, museum curator, opera director giving notes. Emotion: calm, precise, otherworldly distance. Light alto with breathy edge and very precise consonants. Slow deliberate pace with calm pauses, subtle distance like she is translating from a language she knows better. Faint mid-Atlantic or placeless American academic accent. Avoid ethereal-fey, sing-song, breathy-spirit, or witchy delivery.`,
    },
  },
  serath: {
    male: {
      voice_id: '',
      direction: `Native English. Male, mid-30s. Excellent audio quality. Persona: funeral director who is genuinely warm, forensic pathologist explaining cause of death over coffee, hospice chaplain. Emotion: calm, faintly formal, matter-of-fact. Mid-low baritone with calm formality, very clean articulation. Even pace, treats grim subjects matter-of-factly with the slight smile of a man who has long since made peace with the dead. Faint East Coast or mid-Atlantic American accent. Avoid death-cult, hooded-villain, whispered-evil, or theatrically-grim delivery.`,
    },
    female: {
      voice_id: 'Rm14i2uPTqCL0k0wW7KI',
      direction: `Native English. Female, mid-30s. Excellent audio quality. Persona: forensic pathologist who tells dad jokes, funeral director everyone loves, estate lawyer who genuinely enjoys probate. Emotion: cool, composed, gently amused. Mid alto with cool composure, even pace and faint formality, very clean diction. Treats dark material as routine and sometimes as funny. Faint East Coast or mid-Atlantic American accent. Avoid breathy-goth, witchy, vampire-femme, or hooded-villain delivery.`,
    },
  },
};

const PARTY_VOICES: Record<string, VoiceCastEntry> = Object.fromEntries(
  RACES.flatMap((race) =>
    GENDERS.map((gender) => {
      const cfg = PARTY_VOICES_BY_RACE[race][gender];
      return [
        partyKey(race, gender),
        {
          voice_id: cfg.voice_id,
          model: cfg.model ?? 'eleven_multilingual_v2',
          voice_settings: cfg.voice_settings ?? { ...PARTY_DEFAULT_SETTINGS },
          direction: cfg.direction,
        } satisfies VoiceCastEntry,
      ];
    }),
  ),
);

export const CAST: Record<string, VoiceCastEntry> = { ...NPCS, ...PARTY_VOICES };

/** Set of `{race}_{gender}` keys whose party voice has been cast (non-empty
 *  voice_id). Used at runtime by the briefing resolver to prefer voiced strands
 *  when picking which party member speaks a line — silent strands are only
 *  chosen when the entire party is uncast. */
export const VOICED_PARTY_VARIANTS: ReadonlySet<string> = new Set(
  RACES.flatMap((race) =>
    GENDERS
      .filter((gender) => PARTY_VOICES_BY_RACE[race][gender].voice_id !== '')
      .map((gender) => `${race}_${gender}`),
  ),
);

export function getVoice(key: string): VoiceCastEntry {
  const entry = CAST[key];
  if (!entry) {
    throw new Error(
      `[cast] No voice configured for "${key}". Add an entry in scripts/voiceover/cast.ts.`,
    );
  }
  if (!entry.voice_id) {
    throw new Error(
      `[cast] voice_id is empty for "${key}". Pick a voice from https://elevenlabs.io/app/voice-library and paste the ID into cast.ts.`,
    );
  }
  return entry;
}
